import { CHART_COLORS, CHART_VIEW } from "../constants";
import {
  createFrame,
  drawCrosshair,
  drawEntry,
  drawMarker,
  drawPriceAxis,
  ENTRY_POINT_INDEX,
  getCandleSlot,
  getCandleX,
  getToneColor,
} from "./chartFrame";

// Share of a candle's slot its body takes
const BODY_RATIO = 0.6;
const MIN_BODY_WIDTH = 1;

/**
 * Candles of a trade after its entry point, with the entry line and markers.
 *
 * @param {CanvasRenderingContext2D} ctx scaled to CSS pixels
 * @param {object} params
 * @param {number} params.width CSS pixels
 * @param {number} params.height CSS pixels
 * @param {object[]} params.candles shown so far
 * @param {object[]} params.rangeCandles all candles, they set the price range
 * @param {number|null} params.entryPrice
 * @param {number|null} params.exitPrice of a closed trade
 * @param {boolean} params.isDone the replay has played, the exit marker shows
 * @param {boolean} params.isProfit colors the exit marker
 * @param {{index: number, price: number, label: string}|null} params.hover candle under the pointer
 * @param {(price: number) => string} params.formatPrice
 * @returns {{x: number, y: number}|null} the entry point
 */
export const drawCandles = (
  ctx,
  {
    width,
    height,
    candles,
    rangeCandles,
    entryPrice,
    exitPrice,
    isDone,
    isProfit,
    hover,
    formatPrice,
  },
) => {
  const prices = rangeCandles.flatMap(({ high, low }) => [high, low]);

  if (entryPrice != null) prices.push(entryPrice);

  const frame = createFrame({ width, height, prices });
  const { plotWidth, toY } = frame;
  const slot = getCandleSlot(CHART_VIEW.CANDLES, plotWidth, rangeCandles.length);
  const toX = (index) =>
    getCandleX(CHART_VIEW.CANDLES, index, plotWidth, rangeCandles.length);
  const bodyWidth = Math.max(MIN_BODY_WIDTH, slot * BODY_RATIO);

  drawPriceAxis(ctx, frame, formatPrice);

  candles.forEach((candle, index) => {
    const x = toX(index);
    const color =
      candle.close >= candle.open ? CHART_COLORS.up : CHART_COLORS.down;
    const top = toY(Math.max(candle.open, candle.close));
    const bottom = toY(Math.min(candle.open, candle.close));

    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(Math.round(x) + 0.5, toY(candle.high));
    ctx.lineTo(Math.round(x) + 0.5, toY(candle.low));
    ctx.stroke();
    ctx.fillRect(x - bodyWidth / 2, top, bodyWidth, Math.max(1, bottom - top));
  });

  const entryPoint =
    entryPrice != null
      ? drawEntry(ctx, frame, toX(ENTRY_POINT_INDEX), entryPrice)
      : null;

  if (isDone && entryPrice != null && exitPrice != null) {
    drawMarker(
      ctx,
      toX(candles.length - 1),
      toY(exitPrice),
      getToneColor(isProfit),
    );
  }

  if (hover) {
    drawCrosshair(ctx, frame, {
      x: toX(hover.index),
      price: hover.price,
      label: hover.label,
    });
  }

  return entryPoint;
};
